import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { getMapYears, getMetricsFor, getProvincesMetrics, MapAutonomousCommunity, MapMunicipality, MapProvince, MapProvinceMetric, MapSectionMetric, MapYear, useGetProvincesMetrics } from '../services/map-municipalities.service'
import { MapMetric } from '../services/map-metrics.service'
import { Map } from './Map'
import { ErrorState } from './ErrorState'
import { LoadingOverlay } from './LoadingOverlay';
import { useGetProvincesTopojson } from '../services/map-topojson.service'
import { GeoJSON } from 'highcharts'
import { getGeojson } from '../services/map-geojson.service';
import { AVISO_VISTA_PROVINCIAL, AvisoZona, avisoDeZona, esZonaDemasiadoExtensa, estadoDeError } from '../services/map-error.util';
import { ADD_ALL_ID } from '../layouts/components/map-municipality-search/MapMunicipalitySearchForm';
import { filterAndRoundByYear } from './map-helper.util';

type Props = {
  autonomousCommunities: MapAutonomousCommunity[],
  provinces: MapProvince[],
  municipalities: MapMunicipality[],
  metric: MapMetric,
}

export const MapHelper: React.FC<Props> = ({ autonomousCommunities, provinces, municipalities, metric }) => {
  // We are going to use topojson for the whole spain map, since it is smaller
  const { loading: loadingProvincesTopojson, data: provincesTopojson } = useGetProvincesTopojson();

  const [sectionGeojson, setSectionGeojson] = useState<GeoJSON>();
  const [metricsData, setMetricsData] = useState<MapSectionMetric[]>([])
  const [loadingSection, setLoadingSection] = useState(false);

  const [provincesMetricsData, setProvincesMetricsData] = useState<MapProvinceMetric[]>([])
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Si la zona no se puede cargar, avisamos en pantalla en vez de dejar el mapa vacio en silencio
  const [errorZona, setErrorZona] = useState<AvisoZona>();
  // Si la zona es demasiado extensa para el detalle por secciones, caemos a la vista provincial
  const [vistaProvincialPorTamano, setVistaProvincialPorTamano] = useState(false);

  const [years, setYears] = useState<MapYear[]>([]);
  const [selectedYear, setSelectedYear] = useState<MapYear>();

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getMapYears(controller.signal).then(data => {
      if (data) {
        setYears(data);
        setSelectedYear(data[0]);
      }
    }).catch(err => {
      if (err.name !== 'AbortError') {
        console.error('Error fetching map years:', err);
      }
    });

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (autonomousCommunities.some(it => it.id === ADD_ALL_ID) || !(autonomousCommunities.length + provinces.length + municipalities.length)) {
      setSectionGeojson(undefined);
      setErrorZona(undefined);
      setVistaProvincialPorTamano(false);
      setProvincesMetricsData([]);
      setLoadingMetrics(true);
      getProvincesMetrics(metric.id, controller.signal)
        .then(data => {
          if (data) {
            setProvincesMetricsData(data);
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('[MapHelper] Error fetching province metrics:', err);
          }
        })
        .finally(() => setLoadingMetrics(false));
      
      return () => controller.abort();
    }
    setLoadingSection(true);
    setErrorZona(undefined);
    setVistaProvincialPorTamano(false);
    setSectionGeojson(undefined);
    setMetricsData([]);
    Promise.all([
      getGeojson({ autonomousCommunities, provinces, municipalities, signal: controller.signal }).then(data => {
        setSectionGeojson(data);
      }),
      getMetricsFor(metric.id, { autonomousCommunities, provinces, municipalities }, controller.signal).then(data => {
        setMetricsData(data);
      })
    ]).catch(err => {
      if (err.name === 'AbortError') {
        return;
      }
      const status = estadoDeError(err);
      if (esZonaDemasiadoExtensa(status)) {
        // El API no puede servir el detalle por secciones de una zona tan grande: en vez de
        // dejar la zona vacia, pintamos el mapa por provincias y lo explicamos.
        setVistaProvincialPorTamano(true);
        setErrorZona(undefined);
        setLoadingMetrics(true);
        getProvincesMetrics(metric.id, controller.signal)
          .then(data => {
            if (data) {
              setProvincesMetricsData(data);
            }
          })
          .catch(errProvincias => {
            if (errProvincias.name !== 'AbortError') {
              console.error('[MapHelper] Error fetching province metrics:', errProvincias);
              setVistaProvincialPorTamano(false);
              setErrorZona(avisoDeZona(estadoDeError(errProvincias)));
            }
          })
          .finally(() => setLoadingMetrics(false));
        return;
      }
      console.error('[MapHelper] Error fetching section data:', err);
      setErrorZona(avisoDeZona(status));
    }).finally(() => setLoadingSection(false));

    return () => {
      controller.abort();
    };
  },
    [metric, autonomousCommunities, provinces, municipalities])

  const loading = loadingProvincesTopojson || loadingMetrics || loadingSection || !selectedYear;

  const mapContent = sectionGeojson
    ? (
      <Map
        autonomousCommunities={autonomousCommunities}
        provinces={provinces}
        municipalities={municipalities}
        metric={metric}
        year={selectedYear}
        geographicalData={sectionGeojson}
        metricData={selectedYear 
          ? filterAndRoundByYear(metricsData, selectedYear.name)
          : []
        }
        seriesOptions={{
          joinBy: ['id', 'section_id'], tooltip: {
            footerFormat: '<span style="font-size:10px">Distrito {point.point.properties.district}, Sección {point.point.properties.section}</span>'
          },
        }}
        years={years}
        handleYearSelection={setSelectedYear}
      ></Map>
    )
    : (
      <Map
        autonomousCommunities={autonomousCommunities}
        provinces={provinces}
        municipalities={municipalities}
        metric={metric}
        year={selectedYear}
        geographicalData={provincesTopojson}
        metricData={selectedYear 
          ? filterAndRoundByYear(provincesMetricsData, selectedYear.name)
          : []
        }
        seriesOptions={{ joinBy: ['CPRO', 'province_id'] }}
        years={years}
        handleYearSelection={setSelectedYear}
      ></Map>
    );

  return (
    <LoadingOverlay active={loading}>
      {errorZona ? (
        <ErrorState title={errorZona.title} message={errorZona.message} fullPage={false} />
      ) : (
        <>
          {vistaProvincialPorTamano && (
            <AvisoVistaProvincial role="status">{AVISO_VISTA_PROVINCIAL}</AvisoVistaProvincial>
          )}
          {mapContent}
        </>
      )}
    </LoadingOverlay>
  );
}

/** Aviso dentro de la seccion (no tapa el mapa): tinte de marca con texto en tinta. */
const AvisoVistaProvincial = styled.p`
  margin: 0;
  padding: 12px 16px;
  background-color: rgba(47, 205, 222, 0.14);
  color: #222;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
`
